/**
 * V2.0.30 — Wall Violation Highlight Layer.
 *
 * Renders coral-red wall-segment overlays on the 2D floor plan canvas
 * for rooms with active building-code violations. The visual cue lets
 * the user immediately see WHERE the issue is rather than reading a
 * dialog list.
 *
 * Color by severity (intentionally muted so it reads as a CAD callout
 * rather than a UI error toast):
 *   critical → coral-red  (#fb7185)
 *   warning  → amber      (#fbbf24)
 *   advisory → brass-gold (#c4a96b)
 *
 * Selection of WHICH walls light up per violation:
 *   - category "egress"        → exterior walls of the room (window add)
 *   - id starts "front_door_*" → south (lowest-z) exterior wall only
 *   - id starts "door_swing_*" → no wall highlight (door arc is separate)
 *   - id starts "stair_*"      → no wall highlight (stair geometry)
 *   - otherwise (room_id given)→ ALL exterior walls of that room
 *
 * The component is intentionally pointer-events:none so it never
 * intercepts canvas clicks.
 */
import React from "react";

const SEVERITY_STROKE = {
  critical: "#fb7185", // coral-red (rose-400)
  warning: "#fbbf24",  // amber-400
  advisory: "#c4a96b", // brass-400
};
const SEVERITY_RANK = { critical: 0, warning: 1, advisory: 2 };

function roomWallSegments(room) {
  // Returns four wall edges keyed by side: north (high z), south (low z),
  // east (high x), west (low x). Each segment is {axis, coord, lo, hi}.
  const x0 = Number(room.x) || 0;
  const z0 = Number(room.z) || 0;
  const w = Number(room.width) || 0;
  const d = Number(room.depth) || 0;
  return {
    north: { axis: "x", coord: z0 + d, lo: x0, hi: x0 + w },
    south: { axis: "x", coord: z0,     lo: x0, hi: x0 + w },
    east:  { axis: "z", coord: x0 + w, lo: z0, hi: z0 + d },
    west:  { axis: "z", coord: x0,     lo: z0, hi: z0 + d },
  };
}

export default function WallViolationLayer({
  violations,
  rooms,
  floorIndex,
  toX,
  toY,
}) {
  if (!Array.isArray(violations) || violations.length === 0) return null;
  if (!Array.isArray(rooms) || rooms.length === 0) return null;

  const byId = new Map(rooms.map((r) => [r.id, r]));

  // Build a map: "axis|coord|lo|hi" → highest-severity violation summary.
  const segMap = new Map();
  const addSeg = (seg, v) => {
    const key = `${seg.axis}|${seg.coord.toFixed(2)}|${seg.lo.toFixed(2)}|${seg.hi.toFixed(2)}`;
    const existing = segMap.get(key);
    if (!existing ||
        SEVERITY_RANK[v.severity] < SEVERITY_RANK[existing.severity]) {
      segMap.set(key, { ...seg, severity: v.severity, label: v.title });
    }
  };

  for (const v of violations) {
    const loc = v?.location || {};
    if (Number(loc.floor_index) !== Number(floorIndex)) continue;
    if (!loc.room_id) continue;
    const room = byId.get(loc.room_id);
    if (!room) continue;
    const walls = roomWallSegments(room);
    const id = String(v.id || "");
    if (v.category === "egress" || id.startsWith("egress_")) {
      // Window-add — all four exterior walls are candidates.
      Object.values(walls).forEach((seg) => addSeg(seg, v));
    } else if (id.startsWith("front_door_")) {
      addSeg(walls.south, v);
    } else if (id.startsWith("door_swing_") || id.startsWith("stair_")) {
      // Not wall-related — skip.
      continue;
    } else {
      // Default — outline the offending room.
      Object.values(walls).forEach((seg) => addSeg(seg, v));
    }
  }

  if (segMap.size === 0) {
    return <g data-testid="wall-violation-layer" pointerEvents="none" />;
  }

  return (
    <g data-testid="wall-violation-layer" pointerEvents="none">
      {Array.from(segMap.values()).map((seg, i) => {
        const stroke = SEVERITY_STROKE[seg.severity] || SEVERITY_STROKE.advisory;
        const isHoriz = seg.axis === "x";
        const x1 = isHoriz ? toX(seg.lo)   : toX(seg.coord);
        const x2 = isHoriz ? toX(seg.hi)   : toX(seg.coord);
        const y1 = isHoriz ? toY(seg.coord): toY(seg.lo);
        const y2 = isHoriz ? toY(seg.coord): toY(seg.hi);
        return (
          <g key={`${seg.axis}-${seg.coord}-${i}`}>
            {/* Subtle outer glow for visibility on light + dark plans */}
            <line
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={stroke}
              strokeOpacity={0.18}
              strokeWidth={10}
              strokeLinecap="round"
            />
            <line
              data-testid={`wall-violation-${seg.severity}`}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={stroke}
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray="10 5"
            >
              <title>{seg.label}</title>
            </line>
          </g>
        );
      })}
    </g>
  );
}
